import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { msalInstance } from './lib/auth'
import { ThemeProvider } from './components/theme/ThemeProvider'
import { AppLayout } from './components/layout/AppLayout'
import { AuthGuard } from './components/layout/AuthGuard'
import { CurrentUserProvider } from './components/layout/CurrentUserProvider'
import { PermissionGuard } from './components/layout/PermissionGuard'
import './index.css'

// Lazy-loaded pages
import { lazy, Suspense } from 'react'
const OverviewPage        = lazy(() => import('./pages/OverviewPage'))
const IdentitiesPage      = lazy(() => import('./pages/IdentitiesPage'))
const IdentityPage        = lazy(() => import('./pages/IdentityPage'))
const DevicesPage         = lazy(() => import('./pages/DevicesPage'))
const DevicePage          = lazy(() => import('./pages/DevicePage'))
const LicensesPage        = lazy(() => import('./pages/LicensesPage'))
const LicenseCatalogPage       = lazy(() => import('./pages/LicenseCatalogPage'))
const LicenseCatalogDetailPage = lazy(() => import('./pages/LicenseCatalogDetailPage'))
const ServicesPage        = lazy(() => import('./pages/ServicesPage'))
const ServiceDetailPage   = lazy(() => import('./pages/ServiceDetailPage'))
const AccessProfilesPage       = lazy(() => import('./pages/AccessProfilesPage'))
const AccessProfileDetailPage  = lazy(() => import('./pages/AccessProfileDetailPage'))
const SettingsPage        = lazy(() => import('./pages/SettingsPage'))
const TicketsPage         = lazy(() => import('./pages/TicketsPage'))
const TicketDetailPage    = lazy(() => import('./pages/TicketDetailPage'))
const SecurityPage        = lazy(() => import('./pages/SecurityPage'))
const SyncPage            = lazy(() => import('./pages/SyncPage'))
const LogsPage            = lazy(() => import('./pages/LogsPage'))
const ReportsPage         = lazy(() => import('./pages/ReportsPage'))
const UptimeProductPage   = lazy(() => import('./pages/uptime/UptimeProductPage'))
const UptimeEventPage     = lazy(() => import('./pages/uptime/UptimeEventPage'))
const UptimeEndpointPage  = lazy(() => import('./pages/uptime/UptimeEndpointPage'))
const UptimeCatalogPage   = lazy(() => import('./pages/uptime/UptimeCatalogPage'))
const AiUsagePage         = lazy(() => import('./pages/AiUsagePage'))
const AiUsagePersonPage   = lazy(() => import('./pages/AiUsagePersonPage'))
const OrganizationPage          = lazy(() => import('./pages/OrganizationPage'))
const OrganizationBulkImportPage = lazy(() => import('./pages/OrganizationBulkImportPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,          // 1 minute — data is near-real-time not live
      gcTime: 5 * 60_000,         // 5 minute cache
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

createRoot(root).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthGuard>
              <CurrentUserProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route element={<AppLayout />}>
                      {/* Overview — fallback landing page, no guard */}
                      <Route index element={<OverviewPage />} />
                      <Route path="overview" element={<OverviewPage />} />

                      {/* Guarded pages */}
                      <Route path="identities" element={<PermissionGuard page="identities"><IdentitiesPage /></PermissionGuard>} />
                      <Route path="identities/:id" element={<PermissionGuard page="identities"><IdentityPage /></PermissionGuard>} />
                      <Route path="devices" element={<PermissionGuard page="devices"><DevicesPage /></PermissionGuard>} />
                      <Route path="devices/:id" element={<PermissionGuard page="devices"><DevicePage /></PermissionGuard>} />
                      <Route path="licenses" element={<PermissionGuard page="licenses"><LicensesPage /></PermissionGuard>} />
                      <Route path="license-catalog" element={<PermissionGuard page="license_catalog"><LicenseCatalogPage /></PermissionGuard>} />
                      <Route path="license-catalog/:id" element={<PermissionGuard page="license_catalog"><LicenseCatalogDetailPage /></PermissionGuard>} />
                      <Route path="services" element={<PermissionGuard page="services"><ServicesPage /></PermissionGuard>} />
                      <Route path="services/:id" element={<PermissionGuard page="services"><ServiceDetailPage /></PermissionGuard>} />
                      <Route path="service-profiles" element={<PermissionGuard page="services"><AccessProfilesPage /></PermissionGuard>} />
                      <Route path="service-profiles/:id" element={<PermissionGuard page="services"><AccessProfileDetailPage /></PermissionGuard>} />
                      <Route path="settings" element={<PermissionGuard page="settings"><SettingsPage /></PermissionGuard>} />
                      <Route path="tickets" element={<PermissionGuard page="tickets"><TicketsPage /></PermissionGuard>} />
                      <Route path="tickets/:id" element={<PermissionGuard page="tickets"><TicketDetailPage /></PermissionGuard>} />
                      <Route path="security" element={<PermissionGuard page="security"><SecurityPage /></PermissionGuard>} />
                      <Route path="sync" element={<PermissionGuard page="sync"><SyncPage /></PermissionGuard>} />
                      <Route path="logs" element={<PermissionGuard page="logs"><LogsPage /></PermissionGuard>} />
                      <Route path="reports" element={<PermissionGuard page="reports"><ReportsPage /></PermissionGuard>} />
                      {/* Uptime drill-downs (ADR-016); the tab itself is /reports?tab=uptime */}
                      <Route path="reports/uptime/products/:id" element={<PermissionGuard page="reports"><UptimeProductPage /></PermissionGuard>} />
                      <Route path="reports/uptime/events/:key" element={<PermissionGuard page="reports"><UptimeEventPage /></PermissionGuard>} />
                      <Route path="reports/uptime/endpoints/:key" element={<PermissionGuard page="reports"><UptimeEndpointPage /></PermissionGuard>} />
                      <Route path="reports/uptime/catalog" element={<PermissionGuard page="reports"><UptimeCatalogPage /></PermissionGuard>} />
                      {/* URL is hyphenated like /license-catalog; the permission key is ai_usage */}
                      <Route path="ai-usage" element={<PermissionGuard page="ai_usage"><AiUsagePage /></PermissionGuard>} />
                      <Route path="ai-usage/people/:identityId" element={<PermissionGuard page="ai_usage"><AiUsagePersonPage /></PermissionGuard>} />
                      <Route path="organization" element={<PermissionGuard page="organization"><OrganizationPage /></PermissionGuard>} />
                      <Route path="organization/bulk-import" element={<PermissionGuard page="organization"><OrganizationBulkImportPage /></PermissionGuard>} />
                    </Route>
                  </Routes>
                </Suspense>
              </CurrentUserProvider>
            </AuthGuard>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>
)

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: 'var(--bg-base)',
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        border: '2px solid var(--border-default)',
        borderTopColor: 'var(--brand-coral)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
